import React, { useCallback, useEffect, useMemo, useState } from "react";

import useControlPanel from "src/hooks/useControlPanel";
import Pagination from "src/components/Pagination/Pagination";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import ReleaseOSControlPanel from "../../components/ControlPanel/ReleaseOSControlPanel";
import ReleaseOSTable from "../../components/Table/ReleaseOSTable";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import { useGetPermissionAccessControl } from "src/context/PermissionAccessControl";
import {
  createReleaseAppOS,
  getReleaseAppOS,
  updateReleaseAppOS,
} from "src/apis/VersionRelease/operatingSystem.api";
import { path } from "src/constants/path";
import { CACHE_RELEASE_APP_OS } from "src/constants/actionCache";
import { NotifyConfirm } from "src/components/notify/Notify";
import { dataStateType } from "src/constants/status";

const initReleaseOSData = {
  list: [],
  total_record: 0,
};

const titlePage = "Operating Systems";

const ReleaseOperatingSystem = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();

  // cache
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  const [releaseOSData, setReleaseOSData] = useState(initReleaseOSData);
  const [filterReleaseOss, setFilterReleaseOSs] = useState([]);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const permissionsAccessControl = useGetPermissionAccessControl();

  const [errorsForm, setErrorsForm] = useState({});

  const fetchReleaseOSs = useCallback(() => {
    getReleaseAppOS({}, (result) => {
      setReleaseOSData({ ...result });
      setFilterReleaseOSs([...result.list]);
    });
  }, []);

  useEffect(() => {
    fetchReleaseOSs();
  }, [fetchReleaseOSs]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const updateFilteredReleaseOSs = useCallback((releaseOSs) => {
    setFilterReleaseOSs(releaseOSs);
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (releaseOSs, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return releaseOSs.filter((releaseOS) => {
        return releaseOS.name.toLowerCase().includes(key.toLowerCase().trim());
      });
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredReleaseOSs = value
        ? searchByKeyWord(releaseOSData.list, value)
        : releaseOSData.list;
      updateFilteredReleaseOSs(filteredReleaseOSs);
    },
    [releaseOSData.list, searchByKeyWord, updateFilteredReleaseOSs]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  // === refactor === //
  const addOrEdit = useCallback(
    (pairingType, resetForm) => {
      if (pairingType.id) {
        updateReleaseAppOS(pairingType.id, pairingType, handleApiResponse);
      } else {
        createReleaseAppOS(pairingType, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchReleaseOSs();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_RELEASE_APP_OS);
        }
      }
    },
    [fetchReleaseOSs, setCurrentActionCache]
  );

  const handleDelete = (releaseOSDeleted) => {
    NotifyConfirm("warning", "Are you delete it ?", (result) => {
      if (result.isConfirmed) {
        console.log("implement feature delete", releaseOSDeleted);
      }
    });
  };

  /* ---- result for render ----- */
  const resultFilter = useMemo(
    () => searchByKeyWord(filterReleaseOss, searchKey),
    [filterReleaseOss, searchByKeyWord, searchKey]
  );
  const listItem = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>{titlePage}</h2>
      </div>

      <ReleaseOSControlPanel
        handleOpenPopup={handleOpenPopup}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={true}
        />
      </ReleaseOSControlPanel>

      <ReleaseOSTable
        permissionsAccessControl={permissionsAccessControl}
        data={listItem}
        handleOpenPopup={handleOpenPopup}
        handleDelete={handleDelete}
        status={status}
      />

      <Pagination
        pathname={path.releaseAppVersion}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default ReleaseOperatingSystem;
